import { useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import {
  AnketaActionsEnum,
  AnketaResourcesEnum,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";

const i18n = "anketa.opsanio.appliance.flyout.aiMail";

/**
 * Send the AI-gateway budget overview to this appliance's cost-alert recipients.
 *
 * Rendered only for an appliance whose AI feature is on - the mail is about a
 * model gateway, so on an AI-less VPC there is nothing to report and nobody to
 * report it to. That decision belongs to `features.tsx`, which already holds the
 * parsed feature flags, so this component is simply not mounted otherwise.
 *
 * Two deliberate constraints:
 *
 *  - **The operator picks the appliance, not the audience.** Recipients are read
 *    server-side from `ai.alerting.email.receiverEmails`; this component only
 *    *shows* them so nobody sends blind. There is no field to add an address -
 *    that would turn the route into a way to mail anyone a branded opsanio mail.
 *  - **It confirms before sending.** This leaves anketa and lands in a
 *    customer's inbox, so the button opens a dialog naming every recipient
 *    rather than firing on the first click.
 */
export const OpsanioApplianceAiBudgetMail = ({
  appliance,
  recipients,
}: {
  appliance: OpsanioApplianceData;
  /** `ai.alerting.email.receiverEmails` from the questionnaire facts. */
  recipients: string[];
}): JSX.Element | null => {
  const { Post } = useApi();
  const { keycloak, checkPermission } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [open, setOpen] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);

  // Affordance only - `POST /ai/settings-mail/:name` re-checks the Opsanio
  // baseline gate AND `opsanio/contact · create { isInternal }`.
  const maySend = checkPermission(
    AnketaResourcesEnum.opsanioContact,
    AnketaActionsEnum.create,
  );
  if (!maySend) return null;

  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const audience = recipients.filter((r) => r.trim() !== "");
  // The route answers 400 for an empty recipient list. Saying so up front beats
  // letting the operator discover it by pressing the button.
  const canSend = instanceName !== "" && audience.length > 0;

  const send = async () => {
    setSending(true);
    const response = await Post<any>({
      path: `v2/${AnketaResourcesEnum.opsanio}/ai/settings-mail/${encodeURIComponent(
        instanceName,
      )}`,
      value: {
        questionnaireId: appliance.id,
        ...(note.trim() === "" ? {} : { note: note.trim() }),
      },
      responseType: "json",
      token: keycloak?.token,
    });
    setSending(false);

    if (response.success) {
      const sent: string[] = response.data?.data?.sent ?? audience;
      addToast({
        color: "success",
        iconType: "checkInCircleFilled",
        title: translate(`${i18n}.sent.title`, {
          defaultMessage: "AI budget overview sent",
        }),
        text: translate(`${i18n}.sent.body`, {
          defaultMessage: "Sent to {{count}} recipient(s).",
          values: { count: sent.length },
        }),
      });
      setOpen(false);
      setNote("");
      return;
    }

    addToast({
      color: "danger",
      iconType: "crossInCircleFilled",
      title: translate(`${i18n}.failed.title`, {
        defaultMessage: "Could not send the AI budget overview",
      }),
      text: response.error?.message ?? `HTTP ${response.status}`,
    });
  };

  return (
    <>
      <EuiSpacer size="s" />
      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            iconType="email"
            onClick={() => setOpen(true)}
            disabled={!canSend}
          >
            {translate(`${i18n}.button`, {
              defaultMessage: "Send AI budget overview",
            })}
          </EuiButton>
        </EuiFlexItem>
        {!canSend && (
          <EuiFlexItem>
            <EuiText size="xs" color="subdued">
              {instanceName === ""
                ? translate(`${i18n}.noInstance`, {
                    defaultMessage: "This appliance has no VPC name.",
                  })
                : translate(`${i18n}.noRecipients`, {
                    defaultMessage:
                      "No AI cost-alert recipients are configured on the questionnaire, so there is nobody to send this to.",
                  })}
            </EuiText>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {open && (
        <EuiModal onClose={() => setOpen(false)}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <h2>
                {translate(`${i18n}.modal.title`, {
                  defaultMessage: "Send the AI budget overview?",
                })}
              </h2>
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            <EuiText size="s">
              <p>
                {translate(`${i18n}.modal.body`, {
                  defaultMessage:
                    "This mails the AI gateway's spend limits, alert thresholds and who is notified to the appliance's cost-alert recipients. The customer's technical and sales contacts are copied.",
                })}
              </p>
            </EuiText>
            <EuiSpacer size="m" />

            <EuiFormRow
              label={translate(`${i18n}.modal.recipients`, {
                defaultMessage: "Recipients",
              })}
              helpText={translate(`${i18n}.modal.recipientsHelp`, {
                defaultMessage:
                  "Read from the questionnaire (ai.alerting.email.receiverEmails). Change the list there, not here.",
              })}
            >
              <EuiFlexGroup gutterSize="xs" wrap responsive={false}>
                {audience.map((address) => (
                  <EuiFlexItem grow={false} key={address}>
                    <EuiBadge color="hollow">{address}</EuiBadge>
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </EuiFormRow>

            <EuiFormRow
              label={translate(`${i18n}.modal.note`, {
                defaultMessage: "Note (optional)",
              })}
              helpText={translate(`${i18n}.modal.noteHelp`, {
                defaultMessage:
                  "Markdown. Shown above the figures - e.g. why the budget is being revisited.",
              })}
            >
              <EuiTextArea
                compressed
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </EuiFormRow>

            <EuiSpacer size="s" />
            <EuiCallOut
              size="s"
              color="warning"
              iconType="email"
              title={translate(`${i18n}.modal.warning`, {
                defaultMessage:
                  "This leaves anketa and lands in the customer's inbox.",
              })}
            />
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButtonEmpty onClick={() => setOpen(false)} disabled={sending}>
              {translate("anketa.action.cancel", { defaultMessage: "Cancel" })}
            </EuiButtonEmpty>
            <EuiButton fill iconType="email" isLoading={sending} onClick={send}>
              {translate(`${i18n}.modal.confirm`, {
                defaultMessage: "Send now",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
};

export default OpsanioApplianceAiBudgetMail;
